import React, {useEffect, useMemo, useRef, useState} from 'react';
import {cloneDeep, groupBy} from 'lodash';
import VisibilitySensor from 'react-visibility-sensor';
import {HeaderContainer, PageContent, TableWrapper} from './timesheets_team_page_styled';
import {BUTTON_COLOR, BUTTON_STYLE, ELEMENT_TYPE, FILTER_SECTION, FILTER_TYPE} from '../../../constants';
import {TIME_PERIOD} from '../../../forecast-app/my-work-tab/my-timesheets-page/timesheets_change_date_view';
import HeaderBar from '../../../forecast-app/shared/components/headers/header-bar/header_bar';
import {PersonLine} from './PersonLine';
import {isPersonRowExpanded, shouldRenderPersonRow, usePersonRowExpansion} from './usePersonRowExpansion';
import {TableHeader} from './timesheets_team_table_header';
import Moment from 'moment';
import Util from '../../../forecast-app/shared/util/util';
import {FILTER_SECTIONS} from '../../../forecast-app/shared/components/filters/dropdown_section';
import {getFiltersAlphabetically} from '../../../forecast-app/shared/components/filters/filter_util';
import {getCompanyLockedDate, getLockedDate} from '../time-lock/TimeLockUtil';
import {
	hasFeatureFlag,
	hasTimeLocking,
	usingInternalTimeApproval,
	usingTimeApproval,
	usingTimeOffApproval,
} from '../../../forecast-app/shared/util/FeatureUtil';
import {hasNewApprovalStatuses} from '../../../forecast-app/shared/util/ApprovalStatusUtil';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import CustomScrollToNativeScroll from '../../../forecast-app/shared/components/scroll-bars/CustomScrollToNativeScroll';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {getTeamTimesheetsFilters, getTeamTimeshetsPreappliedFilter} from './timesheets_team_filter';
import {getTimeOffSearchQuery} from '../TimeManagementUtil';
import {createRefetchContainer, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import {getSocketConfig} from '../../../forecast-app/my-work-tab/my-timesheets-page/timesheet_socket_handling';
import SageUtil from '../../../forecast-app/shared/util/sage/SageUtil';

// Shared so a person with no entries gets the same array identity on every render; a fresh []
// would change props each time and defeat React.memo(PersonLine). Safe to share because
// useTimeRegistrationEntityGroups copies the array before mutating it.
const EMPTY_TIME_REGS = [];

const TimesheetTeamPage = ({
	viewer,
	relay,
	setSocketConfig,
	setSocketVariables,
	goToToday,
	adjacentPeriod,
	handleDateRange,
	currentViewingDate,
	openAuditLogPersonId,
	setOpenAuditLogPersonId,
}) => {
	const intl = useIntl();
	const urlparam = Util.getUrlQueryParameter('project');
	const preAppliedFilters = urlparam ? getTeamTimeshetsPreappliedFilter(urlparam, viewer.company.allPersons.edges) : null;
	const [filterFunctions, setFilterFunctions] = useState(getTeamTimesheetsFilters(preAppliedFilters));
	const {formatMessage} = intl;

	// Derived once per date change instead of per render and per row: this is handed to the table
	// header and to every PersonLine, and each row used to allocate its own Moment on every
	// render. Sharing one instance also keeps the firstDayOfWeek prop referentially stable.
	// Safe to share because every consumer clones before adding days.
	const firstDayOfWeek = useMemo(() => currentViewingDate.clone().startOf('week'), [currentViewingDate]);
	// Only this path pins rows as mounted, so only this path needs the week-scoped reset. With the
	// flag off nothing is ever pinned and PersonLine stays mounted across a week change exactly as
	// it did before, so expansion survives week navigation there — unchanged behaviour. (PIKA-728)
	const rowVisibilityOptimization = hasFeatureFlag('timesheet_entries_optimization');
	const expansionResetKey = useMemo(
		() => (rowVisibilityOptimization ? firstDayOfWeek.format('YYYY-MM-DD') : 'every-week'),
		[rowVisibilityOptimization, firstDayOfWeek]
	);

	// Expand state for the person rows lives here, at page level, so it outlives the
	// VisibilitySensor unmounting a row as it scrolls out of view. (PIKA-728)
	const {isPersonExpanded, togglePersonExpanded, retainExpanded} = usePersonRowExpansion(expansionResetKey);

	const hasEditPermission = !SageUtil.syncTimeRegsFromIntacct(viewer.company);

	const isUsingTimeApproval = usingTimeApproval(viewer.company.useTimeApproval);
	const isUsingInternalTimeApproval = usingInternalTimeApproval(viewer.company.useInternalTimeApproval);
	const isUsingTimeOffApproval = usingTimeOffApproval(viewer.company.useTimeOffApproval);
	// Hide the column entirely when FF is on but no approval toggle is enabled, so we don't render an empty status column.
	const showApprovalStatusColumn =
		hasNewApprovalStatuses() && (isUsingTimeApproval || isUsingInternalTimeApproval || isUsingTimeOffApproval);
	useEffect(() => {
		const allPersonIdInt = viewer.company.allPersons.edges.map(p => parseInt(atob(p.node.id).replace('Person:', '')));
		setSocketConfig(getSocketConfig(allPersonIdInt), relay.refetch);
	}, []);

	const refetch = variables => {
		/* If adding a loader or something similar that is triggered by this refetch,
		 * remember to remove it when the socket triggered refetch is done, see TimeApprovalPage
		 **/
		relay.refetch(variables);
		setSocketVariables(variables);
	};

	const didMount = useRef(false);

	const sortFunc = (a, b) => {
		const aName = Util.normalizedWord((a.node.firstName || '') + (a.node.lastName || ''));
		const bName = Util.normalizedWord((b.node.firstName || '') + (b.node.lastName || ''));
		if (aName < bName) return -1;
		if (aName > bName) return 1;
		return 0;
	};

	//Avoid to redo the sorting everytime we change the date
	const personList = useMemo(() => {
		return cloneDeep(viewer.company.allPersons.edges).sort(sortFunc);
	}, [viewer.company.allPersons.edges]);

	const companyWeekLocked = useMemo(() => {
		const companyLockedDate = getCompanyLockedDate(viewer.company);
		const endOfWeek = currentViewingDate.clone().endOf('week');
		return companyLockedDate && companyLockedDate.isSameOrAfter(endOfWeek);
	}, [currentViewingDate]);

	useEffect(() => {
		// Only run if not mount
		if (!didMount.current) {
			didMount.current = true;
		} else {
			//we need to fetch now the time entries of each person
			const startDate = currentViewingDate.clone().startOf('week').format('YYYY-MM-DD');

			const endDate = currentViewingDate.clone().endOf('week').format('YYYY-MM-DD');

			refetch({
				startDateString: startDate,
				endDateString: endDate,
				searchQuery: getTimeOffSearchQuery(viewer),
			});
		}
	}, [currentViewingDate]);

	const onLockButtonClick = () => {
		showModal({
			type: MODAL_TYPE.TIME_LOCK,
		});
	};

	const getHeader = () => {
		const leftContent = [];
		const rightContent = [];

		const dateChanger = {
			type: ELEMENT_TYPE.DATE_CHANGER_V2,
			useTodayButton: true,
			timePeriod: TIME_PERIOD.WEEK_VIEW,
			currentViewingDate: currentViewingDate,
			handleTodayButtonClick: goToToday,
			handleAdjacentDateButtonClick: adjacentPeriod.bind(this, false),
			updateDateRange: handleDateRange.bind(this, false),
			boldLongDayMonth: true,
			selectorTooltipEnabled: true,
			selectorTooltipProps: {
				autoPlace: true,
				grey: true,
				infoText: formatMessage({id: 'common.select_date'}),
			},
			tooltipEnabled: true,
			tooltipProps: {
				autoPlace: true,
				infoText: Moment().format('ddd, DD. MMM') + ' ' + Moment().format('YYYY'),
				grey: true,
			},
		};
		leftContent.push(dateChanger);

		if (companyWeekLocked) {
			leftContent.push({
				type: ELEMENT_TYPE.HEADER_LABEL,
				text: intl.formatMessage({id: 'time_lock.week_locked'}),
			});
		}

		if (
			hasTimeLocking(viewer.company.modules) &&
			viewer.company.useTimeLocking &&
			hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS) &&
			hasEditPermission
		) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: intl.formatMessage({id: 'time_lock.set_timesheet_lock'}),
				callback: onLockButtonClick,
				style: BUTTON_STYLE.OUTLINE_THICK,
				color: BUTTON_COLOR.LIGHTGREY,
				dataCy: 'time-lock-button',
			});
		}

		const peopleFilters = [FILTER_TYPE.TEAM_MEMBER, FILTER_TYPE.TEAM, FILTER_TYPE.ROLE];

		if (Util.hasDepartments()) {
			peopleFilters.push(FILTER_TYPE.DEPARTMENT);
		}
		const projectFilters = [];
		const taskFilters = [];
		rightContent.push({
			type: ELEMENT_TYPE.FILTER_V4,
			defaultSection: FILTER_SECTIONS.PEOPLE,
			projectFilters,
			peopleFilters: getFiltersAlphabetically(peopleFilters, intl.formatMessage),
			taskFilters,
			viewer: viewer,
			filterSection: FILTER_SECTION.TEAM_TIMESHEET,
			appliedFiltersName: `timesheets-team-filter-v4`,
			preAppliedFilters: preAppliedFilters,
			onFiltersChange: (_, filterFunctions) => setFilterFunctions(filterFunctions),
		});

		return <HeaderBar leftContent={leftContent} rightContent={rightContent} noPadding />;
	};

	const filterOptions = {teams: viewer.company.teams.edges};

	const matchFilter = person => {
		if (filterFunctions && filterFunctions.personFilter) {
			return filterFunctions.personFilter(person.node, filterOptions);
		}
		return true;
	};

	// Memoised: this used to re-run on every render, and every caret click now re-renders the page.
	// It also hands each row a fresh array otherwise, which defeats useTimeRegistrationEntityGroups'
	// useMemo and re-runs its O(n^2) grouping per row per click.
	const timeRegsPerPerson = useMemo(
		() => groupBy(viewer.company.timeRegistrations.edges, 'node.person.id'),
		[viewer.company.timeRegistrations.edges]
	);

	// One pass deciding who renders at all. Inactive people with no entries are dropped here rather
	// than inside PersonLine, which returned an empty fragment for them: off-screen they still got
	// a 60px placeholder and then collapsed to 0px on becoming visible, shifting everything below
	// up by 60px per person, and the sensor never re-measured because findDOMNode was null.
	// Filtering here also makes `index` the index among *rendered* rows, so the id === 0 anchors
	// (the Userpilot tour target and the first-row shadow) survive a filter hiding the first person.
	const visiblePeople = useMemo(
		() =>
			personList.filter(person => {
				if (!matchFilter(person)) {
					return false;
				}
				return person.node.active || (timeRegsPerPerson[person.node.id] || EMPTY_TIME_REGS).length > 0;
			}),
		[personList, timeRegsPerPerson, filterFunctions]
	);

	// A row that stops rendering — filtered out, or its last entry for the week deleted elsewhere and
	// pushed in by the socket refetch — must not keep its id, or it would come back expanded and
	// pinned without anyone clicking, and could not be collapsed in the meantime.
	const expandablePersonIds = useMemo(
		() =>
			new Set(
				visiblePeople
					.filter(person => (timeRegsPerPerson[person.node.id] || EMPTY_TIME_REGS).length > 0)
					.map(person => person.node.id)
			),
		[visiblePeople, timeRegsPerPerson]
	);
	useEffect(() => {
		retainExpanded(expandablePersonIds);
	}, [expandablePersonIds, retainExpanded]);

	// getLockedDate allocates Moments, so calling it per row per render handed PersonLine a new
	// prop identity every time and defeated React.memo. Rebuilt only when the people or the
	// company's lock settings change.
	const lockedDates = useMemo(() => {
		const dates = new Map();
		visiblePeople.forEach(person => dates.set(person.node.id, getLockedDate(viewer.company, person.node)));
		return dates;
	}, [visiblePeople, viewer.company]);

	return (
		<PageContent>
			<HeaderContainer data-userpilot="timesheets-team-header">{getHeader()}</HeaderContainer>
			<CustomScrollToNativeScroll className="custom-scrollbar-div" hasFocusableContent isPageScroller>
				<TableWrapper>
					<table data-userpilot="timesheets-team-all">
						<TableHeader
							intl={intl}
							firstDayOfWeek={firstDayOfWeek}
							showApprovalStatusColumn={showApprovalStatusColumn}
						/>
						<tbody>
							{visiblePeople.map((person, index) => {
								const personId = person.node.id;
								const timeRegistrations = timeRegsPerPerson[personId] || EMPTY_TIME_REGS;
								// A deactivated person's entries render nothing (EntityLine bails on !person.active), so
								// expanding them is a dead end: caret up over an empty block, pinned there all week.
								const hasEntries = Boolean(person.node.active) && timeRegistrations.length > 0;
								const isExpanded = isPersonRowExpanded({hasEntries, isExpanded: isPersonExpanded(personId)});

								// Built on demand so an off-screen placeholder row does not pay to construct a row it
								// will not render.
								const renderPersonLine = () => (
									<PersonLine
										key={personId}
										id={index}
										viewer={viewer}
										person={person.node}
										firstDayOfWeek={firstDayOfWeek}
										timeRegistrations={timeRegistrations}
										lockedDate={lockedDates.get(personId)}
										hasEditPermission={hasEditPermission}
										isUsingTimeApproval={isUsingTimeApproval}
										isUsingInternalTimeApproval={isUsingInternalTimeApproval}
										isUsingTimeOffApproval={isUsingTimeOffApproval}
										showApprovalStatusColumn={showApprovalStatusColumn}
										openAuditLogPersonId={openAuditLogPersonId}
										setOpenAuditLogPersonId={setOpenAuditLogPersonId}
										isExpanded={isExpanded}
										onToggleExpanded={togglePersonExpanded}
									/>
								);

								if (!rowVisibilityOptimization) {
									return renderPersonLine();
								}

								/* An expanded row stays mounted once it scrolls past. Swapping it for the
								 * placeholder would unmount it, drop the project rows the user is reading and
								 * shrink the page mid-scroll — the collapse PIKA-728 reported. Collapsed rows
								 * still become placeholders, so the optimization is untouched. */
								return (
									<VisibilitySensor
										key={personId}
										scrollCheck={true}
										partialVisibility
										minTopValue={10}
										offset={{top: -15, bottom: -100}}
									>
										{({isVisible}) =>
											shouldRenderPersonRow({
												isVisible,
												isPinned: isExpanded || openAuditLogPersonId === personId,
											}) ? (
												renderPersonLine()
											) : (
												<tr style={{height: '60px'}} />
											)
										}
									</VisibilitySensor>
								);
							})}
						</tbody>
					</table>
				</TableWrapper>
			</CustomScrollToNativeScroll>
		</PageContent>
	);
};

const timesheetsTeamPageQuery = graphql`
	query timesheetsTeamPage_Query($searchQuery: TaskSearchQueryType, $startDateString: String, $endDateString: String) {
		viewer {
			actualPersonId
			component(name: "team-timesheet-page")
			...timesheetsTeamPage_viewer
				@arguments(searchQuery: $searchQuery, startDateString: $startDateString, endDateString: $endDateString)
			company {
				useTimeApproval
			}
		}
	}
`;

export {timesheetsTeamPageQuery};

export default withSocketHandling(
	createRefetchContainer(
		TimesheetTeamPage,
		{
			viewer: graphql`
				fragment timesheetsTeamPage_viewer on Viewer
				@argumentDefinitions(
					searchQuery: {type: "TaskSearchQueryType"}
					startDateString: {type: "String"}
					endDateString: {type: "String"}
				) {
					component(name: "team-timesheet-page")
					id
					harvestUser
					actualPersonId
					email
					backendId
					availableFeatureFlags {
						key
					}
					filters(first: 10000, filterSection: TEAM_TIMESHEET) @connection(key: "Viewer_filters", filters: []) {
						edges {
							node {
								id
								name
								section
								value
								updatedAt
							}
						}
					}
					language
					projectOwner
					company {
						id
						bambooHREnabled
						characterLimit
						lockedPeriodYear
						lockedPeriodMonth
						lockedPeriodDay
						useTimeApproval
						useInternalTimeApproval
						useTimeOffApproval
						useTimeLocking
						sageIntacctConnectionEnabled
						sageIntacctTimeRegsFromForecast
						modules {
							moduleType
						}
						tier
						idleTimes {
							edges {
								node {
									id
									isInternalTime
								}
							}
						}
						timeRegistrations(
							first: 10000000
							startDate: $startDateString
							endDate: $endDateString
							searchQuery: $searchQuery
						) @connection(key: "Company_timeRegistrations", filters: []) {
							edges {
								node {
									id
									day
									month
									year
									minutesRegistered
									billableMinutesRegistered
									billable
									lockedInPeriod
									approvalStatus
									invoiced
									allocationId
									person {
										id
										startDate
										endDate
										monday
										tuesday
										wednesday
										thursday
										friday
										saturday
										sunday
										excludeFromCompanyLockedPeriod
									}
									role {
										id
										name
									}
									notes
									project {
										id
										name
										projectColor
										companyProjectId
										...DeprecatedProjectIndicatorJS_project
										fullAccessToProject
										readOnlyAccess
										billable
									}
									task {
										id
										name
										companyTaskId
										billable
										estimateForecastMinutes
										totalMinutesRegistered
										project {
											id
											name
											companyProjectId
											customProjectId
											...DeprecatedProjectIndicatorJS_project
											projectColor
											fullAccessToProject
											readOnlyAccess
										}
									}
									idleTimeId
									idleTime {
										id
										name
										isInternalTime
									}
									hiBobAllocationId
								}
							}
						}
						teams(first: 1000000) {
							edges {
								node {
									id
									name
									teamPersons(first: 1000000) {
										edges {
											node {
												id
												person {
													id
												}
											}
										}
									}
								}
							}
						}
						allPersons(first: 1000000, excludeClientUsers: true) {
							edges {
								node {
									holidayCalendar {
										id
										holidayCalendarEntries(
											first: 100000
											startDate: $startDateString
											endDate: $endDateString
										) {
											edges {
												node {
													id
													day
													month
													year
												}
											}
										}
										name
									}
									id
									role {
										id
										name
									}
									department {
										id
										name
									}
									firstName
									lastName
									createdAt
									startDate
									endDate
									active
									monday
									tuesday
									wednesday
									thursday
									friday
									saturday
									sunday
									excludeFromCompanyLockedPeriod
									profilePictureId
									assignedProjectIds
									email
								}
							}
						}
					}
				}
			`,
		},
		graphql`
			query timesheetsTeamPageRefetchQuery(
				$searchQuery: TaskSearchQueryType
				$startDateString: String
				$endDateString: String
			) {
				viewer {
					component(name: "team-timesheet-page")
					...timesheetsTeamPage_viewer
						@arguments(searchQuery: $searchQuery, startDateString: $startDateString, endDateString: $endDateString)
				}
			}
		`
	)
);
